import React, { useEffect, useState } from 'react';
import { Menu, Phone, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from './ui/Button';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Lock body/html scroll while the mobile menu is open; restore on close/unmount
  useEffect(() => {
    if (menuOpen) {
      const prevBody = document.body.style.overflow;
      const prevHtml = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevBody;
        document.documentElement.style.overflow = prevHtml;
      };
    }
  }, [menuOpen]);
  const navLinks = [{
    name: 'Services',
    href: '#services'
  }, {
    name: 'Our Work',
    href: '#work'
  }, {
    name: 'Process',
    href: '#process'
  }, {
    name: 'Service Area',
    href: '#service-area'
  }, {
    name: 'Contact',
    href: '#contact'
  }];
  return <header className={`fixed top-0 left-0 right-0 z-[1100] transition-all duration-300 ${isScrolled ? 'bg-charcoal/95 backdrop-blur-md shadow-lg py-3' : 'bg-charcoal py-4 md:py-5'}`} data-id="element-83">
      <div className="container mx-auto px-4 flex items-center justify-between" data-id="element-84">
        {/* Logo */}
        <a href="#" className="flex items-baseline gap-1 z-10" data-id="element-85">
          <span className="text-xl md:text-2xl font-extrabold tracking-tight text-white font-heading" data-id="element-86">
            EPIC PAVERS
          </span>
          <span className="w-2 h-2 rounded-full bg-teal block" data-id="element-87"></span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-id="element-88">
          {navLinks.slice(0, 4).map(link => <a key={link.name} href={link.href} className="text-sm font-medium text-sand hover:text-teal transition-colors" data-id="element-89">
              {link.name}
            </a>)}
        </nav>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-6" data-id="element-90">
          <a href="tel:7027556539" className="flex items-center gap-2 text-sand hover:text-white transition-colors text-sm font-medium" data-id="element-91">
            <Phone className="w-4 h-4 text-teal" data-id="element-92" />
            702-755-6539
          </a>
          <Button asChild className="bg-teal hover:bg-teal-dark text-white font-bold border-none" data-id="element-93">
            <a href="#contact" data-id="element-94">Free Estimate</a>
          </Button>
        </div>

        {/* Mobile actions */}
        <div className="md:hidden flex items-center gap-4" data-id="element-95">
          <Button asChild size="sm" className="bg-teal hover:bg-teal-dark text-white font-bold h-9 px-4" data-id="element-96">
            <a href="#contact" data-id="element-97">Quote</a>
          </Button>
          <button type="button" onClick={() => setMenuOpen(true)} className="flex items-center justify-center w-11 h-11 text-white rounded-md hover:bg-charcoal-soft transition-colors" aria-label="Open menu" aria-expanded={menuOpen} data-id="element-98">
            <Menu className="w-6 h-6" data-id="element-99" />
          </button>
        </div>
      </div>

      {/* Full-screen mobile menu overlay */}
      <AnimatePresence data-id="element-100">
        {menuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="md:hidden fixed inset-0 z-[2000] flex flex-col" style={{
        backgroundColor: '#0f171e'
      }} role="dialog" aria-modal="true" data-id="element-101">
            {/* Top bar: wordmark + close */}
            <div className="flex items-center justify-between px-4 h-[60px] flex-shrink-0" data-id="element-102">
              <a href="#" onClick={() => setMenuOpen(false)} className="flex items-baseline gap-1" data-id="element-103">
                <span className="text-xl font-extrabold tracking-tight text-white font-heading" data-id="element-104">
                  EPIC PAVERS
                </span>
                <span className="w-2 h-2 rounded-full bg-teal block" data-id="element-105"></span>
              </a>
              <button type="button" onClick={() => setMenuOpen(false)} className="flex items-center justify-center w-11 h-11 text-white rounded-md hover:bg-charcoal-soft transition-colors" aria-label="Close menu" data-id="element-106">
                <X className="w-7 h-7" data-id="element-107" />
              </button>
            </div>

            {/* Centered nav */}
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-8 px-6 pb-16" data-id="element-108">
              <nav className="flex flex-col items-center gap-2" data-id="element-109">
                {navLinks.map(link => <a key={link.name} href={link.href} onClick={() => setMenuOpen(false)} className="flex items-center justify-center min-h-[44px] px-4 text-3xl font-bold font-heading text-sand hover:text-teal transition-colors" data-id="element-110">
                    {link.name}
                  </a>)}
              </nav>

              <div className="h-px w-16 bg-charcoal-soft" data-id="element-111" />

              <a href="tel:7027556539" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 min-h-[44px] text-white text-xl font-medium" data-id="element-112">
                <span className="w-11 h-11 rounded-full bg-charcoal-soft flex items-center justify-center" data-id="element-113">
                  <Phone className="w-5 h-5 text-teal" data-id="element-114" />
                </span>
                702-755-6539
              </a>

              <Button asChild size="lg" className="w-full max-w-xs bg-teal hover:bg-teal-dark text-white font-bold h-12" data-id="element-115">
                <a href="#contact" onClick={() => setMenuOpen(false)} data-id="element-116">
                  Get a Free Estimate
                </a>
              </Button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}